export type DetectedGenderKey = 'male' | 'female' | 'nonBinary';

export interface DetectedGenderResult {
  key: DetectedGenderKey;
  matchedSegment: string;
}

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

const genderPatterns: { key: DetectedGenderKey; patterns: RegExp[] }[] = [
  {
    key: 'male',
    patterns: [
      /\bhomme\b/iu,
      /\bgarcon\b/iu,
      /\bmasculin\b/iu,
      /\bmonsieur\b/iu,
      /\bman\b/iu,
      /\bboy\b/iu,
      /\bmale\b/iu,
      /\bcaballero\b/iu,
      /\bvaron\b/iu
    ]
  },
  {
    key: 'female',
    patterns: [
      /\bfemme\b/iu,
      /\bwoman\b/iu,
      /\bgirl\b/iu,
      /\bfeminine\b/iu,
      /\bmadame\b/iu,
      /\bfille\b/iu,
      /\bmujer\b/iu,
      /\bfemenina\b/iu
    ]
  },
  {
    key: 'nonBinary',
    patterns: [
      /non[-\s]?binaire/iu,
      /non[-\s]?binary/iu,
      /non[-\s]?binario/iu,
      /gender[-\s]?fluid/iu,
      /agenre/iu,
      /sans genre/iu
    ]
  }
];

export const detectGenderFromText = (text?: string | null): DetectedGenderResult | null => {
  if (!text) return null;
  const normalizedText = normalize(text);

  for (const entry of genderPatterns) {
    for (const pattern of entry.patterns) {
      const match = normalizedText.match(pattern);
      if (match) {
        return {
          key: entry.key,
          matchedSegment: match[0]
        };
      }
    }
  }

  return null;
};

const localizedLabels: Record<DetectedGenderKey, { fr: string; en: string; es: string }> = {
  male: {
    fr: 'homme',
    en: 'man',
    es: 'hombre'
  },
  female: {
    fr: 'femme',
    en: 'woman',
    es: 'mujer'
  },
  nonBinary: {
    fr: 'personne non binaire',
    en: 'non-binary person',
    es: 'persona no binaria'
  }
};

export const getLocalizedGenderLabel = (
  key: DetectedGenderKey,
  language: 'fr' | 'en' | 'es'
): string => localizedLabels[key][language];
